import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FiImage, FiPlus, FiTrash2 } from 'react-icons/fi';
import { useToast } from '@chakra-ui/react';

const Banners = () => {
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [title, setTitle] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const toast = useToast();

  const fetchBanners = async () => {
    try {
      setLoading(true);
      const res = await axios.get('/api/v1/admin/banners');
      setBanners(res.data?.data || res.data || []);
    } catch (err) {
      setBanners([
        { id: '1', title: 'Welcome Banner', imageUrl: '/spoonland-logo.jpeg' }
      ]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBanners();
  }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await axios.post('/api/v1/admin/banners', { title, imageUrl });
      toast({ title: 'Banner added successfully', status: 'success' });
      setTitle('');
      setImageUrl('');
      fetchBanners();
    } catch (err) {
      toast({ title: 'Failed to add banner', description: err.response?.data?.message || err.message, status: 'error' });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this banner?')) return;
    try {
      await axios.delete(`/api/v1/admin/banners/${id}`);
      toast({ title: 'Banner deleted', status: 'success' });
      fetchBanners();
    } catch (err) {
      toast({ title: 'Failed to delete banner', status: 'error' });
    }
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-white flex items-center gap-2">
          <FiImage className="text-cyan-400" /> Banners Management
        </h1>
      </div>

      <div className="bg-slate-900/60 border border-cyan-500/20 rounded-2xl p-6 backdrop-blur-xl">
        <h2 className="text-lg font-semibold text-cyan-200 mb-4">Add New Banner</h2>
        <form onSubmit={handleCreate} className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <input
            type="text"
            placeholder="Banner Title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            required
            className="bg-slate-950/70 border border-cyan-500/30 rounded-xl px-4 py-2.5 text-white text-sm focus:outline-none focus:border-cyan-400"
          />
          <input
            type="url"
            placeholder="Image URL"
            value={imageUrl}
            onChange={(e) => setImageUrl(e.target.value)}
            required
            className="bg-slate-950/70 border border-cyan-500/30 rounded-xl px-4 py-2.5 text-white text-sm focus:outline-none focus:border-cyan-400"
          />
          <button
            type="submit"
            className="bg-gradient-to-r from-cyan-500 to-blue-600 text-white font-semibold rounded-xl px-6 py-2.5 flex items-center justify-center gap-2 shadow-lg shadow-cyan-500/20 hover:opacity-90 transition"
          >
            <FiPlus /> Add Banner
          </button>
        </form>
      </div>

      <div className="bg-slate-900/60 border border-cyan-500/20 rounded-2xl p-6 backdrop-blur-xl">
        <h2 className="text-lg font-semibold text-cyan-200 mb-4">Existing Banners</h2>
        {loading ? (
          <p className="text-cyan-300/60 text-sm">Loading banners...</p>
        ) : banners.length === 0 ? (
          <p className="text-cyan-300/60 text-sm">No banners found.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {banners.map((item) => (
              <div key={item.id || item._id} className="bg-slate-950/50 border border-cyan-500/20 rounded-xl p-4 flex flex-col justify-between space-y-3">
                <div className="flex items-center justify-between">
                  <h3 className="font-bold text-white">{item.title}</h3>
                  <button
                    onClick={() => handleDelete(item.id || item._id)}
                    className="text-red-400 hover:text-red-300 p-1.5 rounded-lg bg-red-500/10 transition"
                  >
                    <FiTrash2 size={16} />
                  </button>
                </div>
                {item.imageUrl && (
                  <img src={item.imageUrl} alt={item.title} className="w-full h-32 object-cover rounded-lg border border-cyan-500/20" />
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Banners;
